import { FaceLandmarker, FilesetResolver } from "@mediapipe/tasks-vision";

const WASM_PATH = "/mediapipe/wasm";
const MODEL_PATH = "/mediapipe/face_landmarker.task";

// Iris centres from MediaPipe's 478-point face mesh.
export const RIGHT_IRIS = 468;
export const LEFT_IRIS = 473;

const cache = {};

async function create(runningMode) {
  const vision = await FilesetResolver.forVisionTasks(WASM_PATH);
  const options = { baseOptions: { modelAssetPath: MODEL_PATH }, runningMode, numFaces: 1 };
  try {
    return await FaceLandmarker.createFromOptions(vision, {
      ...options,
      baseOptions: { ...options.baseOptions, delegate: "GPU" },
    });
  } catch {
    return await FaceLandmarker.createFromOptions(vision, {
      ...options,
      baseOptions: { ...options.baseOptions, delegate: "CPU" },
    });
  }
}

export function getFaceLandmarker(runningMode = "VIDEO") {
  if (!cache[runningMode]) {
    cache[runningMode] = create(runningMode).catch((err) => {
      delete cache[runningMode];
      throw err;
    });
  }
  return cache[runningMode];
}

/** Average adult interpupillary distance, used to convert detected pixels into millimetres. */
export const AVERAGE_PD_MM = 63;

/** Real-world width of the frame front, derived from the measurements recorded for that exact frame. */
export function frameWidthMm(product) {
  const lens = Number(product?.lens_width);
  const bridge = Number(product?.bridge_width);
  if (!lens || !bridge) return null;
  return lens * 2 + bridge + 12; // + end pieces
}

/** Maps a normalised landmark onto an object-cover rendered media box. */
export function mapToBox(nx, ny, mediaW, mediaH, boxW, boxH) {
  const mediaAspect = mediaW / mediaH;
  const boxAspect = boxW / boxH;
  let drawW, drawH, offsetX, offsetY;
  if (mediaAspect > boxAspect) {
    drawH = boxH;
    drawW = boxH * mediaAspect;
    offsetX = (boxW - drawW) / 2;
    offsetY = 0;
  } else {
    drawW = boxW;
    drawH = boxW / mediaAspect;
    offsetX = 0;
    offsetY = (boxH - drawH) / 2;
  }
  return { x: offsetX + nx * drawW, y: offsetY + ny * drawH };
}

/** Everything needed to place the overlay: centre, roll angle and width in px. */
export function solveOverlay(landmarks, media, box, product, { scale = 1, offsetY = 0 } = {}) {
  const a = mapToBox(landmarks[LEFT_IRIS].x, landmarks[LEFT_IRIS].y, media.w, media.h, box.w, box.h);
  const b = mapToBox(landmarks[RIGHT_IRIS].x, landmarks[RIGHT_IRIS].y, media.w, media.h, box.w, box.h);
  // Order left-to-right in image space so the roll angle is never flipped 180 degrees.
  const [left, right] = a.x <= b.x ? [a, b] : [b, a];
  const dx = right.x - left.x;
  const dy = right.y - left.y;
  const eyeDist = Math.hypot(dx, dy);
  const widthMm = frameWidthMm(product);
  // Fall back to a generic ratio only when a frame has no recorded measurements.
  const width = (widthMm ? eyeDist * (widthMm / AVERAGE_PD_MM) : eyeDist * 2.3) * scale;
  return {
    x: (left.x + right.x) / 2,
    y: (left.y + right.y) / 2 + offsetY,
    width,
    angle: -(Math.atan2(dy, dx) * (180 / Math.PI)),
    eyeDist,
  };
}
